import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { en, type Dictionary, type TranslationKey } from './en';
import { ar } from './ar';
import { fr } from './fr';
import { id } from './id';
import { tr } from './tr';

export type LangCode = 'en' | 'ar' | 'fr' | 'id' | 'tr';

export interface Language {
    code: LangCode;
    /** In the language itself, which is what a speaker scans for. */
    native: string;
    english: string;
    dir: 'ltr' | 'rtl';
    dict: Dictionary;
}

/**
 * The five languages chosen from where Muslim travellers actually come from:
 * the Gulf, Indonesia and Malaysia, Türkiye and francophone Europe and Africa,
 * plus English as the lingua franca.
 *
 * ⚠️ Translations were produced during the rebuild and have NOT had native
 * review. They should be checked by a speaker before this ships widely —
 * particularly the certificate wording, where a mistranslation could mislead
 * someone about whether food is halal.
 */
export const LANGUAGES: Language[] = [
    { code: 'en', native: 'English', english: 'English', dir: 'ltr', dict: en },
    { code: 'ar', native: 'العربية', english: 'Arabic', dir: 'rtl', dict: ar },
    { code: 'fr', native: 'Français', english: 'French', dir: 'ltr', dict: fr },
    { code: 'id', native: 'Bahasa Indonesia', english: 'Indonesian', dir: 'ltr', dict: id },
    { code: 'tr', native: 'Türkçe', english: 'Turkish', dir: 'ltr', dict: tr },
];

const STORAGE_KEY = 'hy_lang';

function detect(): LangCode {
    try {
        const saved = localStorage.getItem(STORAGE_KEY) as LangCode | null;
        if (saved && LANGUAGES.some((l) => l.code === saved)) return saved;
    } catch { /* private mode */ }
    for (const tag of navigator.languages ?? [navigator.language]) {
        const base = tag.slice(0, 2).toLowerCase();
        // Malay speakers are well served by the Indonesian dictionary; the two
        // are mutually intelligible for copy this short.
        if (base === 'ms') return 'id';
        const hit = LANGUAGES.find((l) => l.code === base);
        if (hit) return hit.code;
    }
    return 'en';
}

/** Replace {slots}. Missing values are left visible rather than silently blank. */
function interpolate(template: string, vars?: Record<string, string | number>): string {
    if (!vars) return template;
    return template.replace(/\{(\w+)\}/g, (whole, key: string) =>
        (key in vars ? String(vars[key]) : whole));
}

interface I18nValue {
    lang: Language;
    setLang: (code: LangCode) => void;
    t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
    /** English plural rule: enough for the handful of counted strings here. */
    tn: (one: TranslationKey, other: TranslationKey, count: number, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
    const [code, setCode] = useState<LangCode>(detect);
    const lang = useMemo(() => LANGUAGES.find((l) => l.code === code) ?? LANGUAGES[0]!, [code]);

    // The whole layout is built on CSS logical properties, so setting `dir` is
    // all that Arabic needs — margins, padding, borders and insets flip on their
    // own rather than needing a mirrored stylesheet.
    useEffect(() => {
        document.documentElement.lang = lang.code;
        document.documentElement.dir = lang.dir;
    }, [lang]);

    const setLang = useCallback((next: LangCode) => {
        setCode(next);
        try { localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode */ }
    }, []);

    const value = useMemo<I18nValue>(() => {
        const t = (key: TranslationKey, vars?: Record<string, string | number>) =>
            // Fall back to English rather than showing a raw key if a
            // translation is ever missing at runtime.
            interpolate(lang.dict[key] ?? en[key] ?? key, vars);
        return {
            lang,
            setLang,
            t,
            tn: (one, other, count, vars) => t(count === 1 ? one : other, { ...vars, count }),
        };
    }, [lang, setLang]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT(): I18nValue {
    const ctx = useContext(I18nContext);
    if (!ctx) throw new Error('useT must be used inside <I18nProvider>');
    return ctx;
}
